import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart, registerables } from 'chart.js';
import '../../styles/components/RainWidget.css';

// Enregistrer tous les composants Chart.js
Chart.register(...registerables);

// Valeurs d'intensité de pluie selon Météo France:
// 1 = Temps sec (pas de pluie)
// 2 = Pluie faible
// 3 = Pluie modérée
// 4 = Pluie forte
// 5-9 = Intensités supplémentaires (utiliser la description fournie par MF)

// Fonction pour déterminer si une prévision indique "temps sec"
const isTempsSec = (forecast) => {
  return forecast.intensity === 1 && forecast.description === "Temps sec";
};

// Fonction pour obtenir l'intensité réelle pour l'affichage
const getDisplayIntensity = (forecast) => {
  return isTempsSec(forecast) ? 0 : forecast.intensity;
};

// Fonction pour déterminer la couleur en fonction de l'intensité de la pluie
const getRainColor = (intensity, description) => {
  if (intensity === 1 && description === "Temps sec") return '#FFFFFF'; // Pas de précipitations
  if (intensity === 2) return '#87CEEB'; // Pluie faible (Bleu ciel)
  if (intensity === 3) return '#00BFFF'; // Pluie modérée (Bleu profond)
  if (intensity >= 4) return '#00008B'; // Pluie forte (Bleu marine foncé)
  return '#A9A9A9'; // Données indisponibles (Gris foncé)
};

// Fonction pour obtenir les couleurs des barres en fonction des prévisions
const getBarColors = (forecasts) => {
  return forecasts.map(forecast => getRainColor(forecast.intensity, forecast.description));
};

// Fonction pour obtenir la description de l'intensité
const getIntensityDescription = (intensity, description) => {
  // Utiliser la description fournie par Météo France si disponible
  if (description) return description;

  // Fallback pour les cas standards
  if (intensity === 1) return 'Temps sec';
  if (intensity === 2) return 'Pluie faible';
  if (intensity === 3) return 'Pluie modérée';
  if (intensity === 4) return 'Pluie forte';
  if (intensity >= 5 && intensity <= 9) return `Pluie très forte (${intensity})`;

  return 'Inconnu';
};

export default function RainWidget({ lat, lon, onRainForecastUpdate, isFrenchPark }) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const isFetching = useRef(false);
  const { t } = useTranslation();

  // Mémoriser la fonction fetchRain avec useCallback
  const fetchRain = useCallback(async () => {
    if (isFetching.current) {
        return;
    }

    isFetching.current = true;
    setLoading(true);
    setError(null);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production'
        ? 'https://api-internal.magicparktimes.com'
        : 'http://localhost:3001');

      let forecastData = [];

      if (isFrenchPark) {
        const url = `${API_BASE_URL}/api/rain?lat=${lat}&lon=${lon}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`${t('weather.apiError')}: ${response.status}`);
        }
        const data = await response.json();

        const nowMs = Date.now();
        const toDeltaMinutes = (tv) => {
          if (tv == null) return null;
          if (typeof tv === 'string') {
            const ts = Date.parse(tv);
            if (!isNaN(ts)) return Math.round((ts - nowMs) / 60000);
          }
          const n = Number(tv);
          if (!isFinite(n)) return null;
          if (n > 1e12) { // epoch ms
            return Math.round((n - nowMs) / 60000);
          }
          if (n > 1e9) { // epoch seconds
            return Math.round((n * 1000 - nowMs) / 60000);
          }
          return n; // minutes relatives
        };

        if (Array.isArray(data?.forecasts)) {
          forecastData = data.forecasts
            .map((f, idx) => ({
              t: toDeltaMinutes(f.t ?? f.time ?? f.timestamp ?? idx * 5),
              intensity: Number(f.intensity ?? 1) || 1,
              description: f.description || getIntensityDescription(Number(f.intensity ?? 1))
            }))
            .filter(f => f.t >= 0 && f.t <= 60)
            .slice(0, 12);
        } else if (Array.isArray(data?.forecast)) {
          forecastData = data.forecast
            .map((f, idx) => ({
              t: toDeltaMinutes(f.t ?? f.time ?? f.timestamp ?? idx * 5),
              intensity: Number(f.intensity ?? 1) || 1,
              description: f.description || getIntensityDescription(Number(f.intensity ?? 1))
            }))
            .filter(f => f.t >= 0 && f.t <= 60)
            .slice(0, 12);
        } else if (Array.isArray(data)) {
          forecastData = data
            .map((v, idx) => ({
              t: idx * 5,
              intensity: Number(v) || 1,
              description: getIntensityDescription(Number(v) || 1)
            }))
            .slice(0, 12);
        } else if (Array.isArray(data?.values)) {
          forecastData = data.values
            .map((v, idx) => (
              typeof v === 'number'
                ? { t: idx * 5, intensity: Number(v) || 1, description: getIntensityDescription(Number(v) || 1) }
                : { t: toDeltaMinutes(v.t ?? v.time ?? v.timestamp ?? idx * 5), intensity: Number(v.intensity ?? 1) || 1, description: v.description || getIntensityDescription(Number(v.intensity ?? 1)) }
            ))
            .filter(f => f.t >= 0 && f.t <= 60)
            .slice(0, 12);
        }
      }

      // Note: Pas de fallback Open‑Meteo. Si indisponible, on affichera "Temps sec" par défaut via la normalisation.

      const normalizeTo12 = (items) => {
        const slots = Array.from({ length: 12 }, (_, i) => ({ t: i * 5, intensity: null, description: null }));
        const sorted = (items || []).slice().sort((a, b) => (a.t ?? 0) - (b.t ?? 0));
        sorted.forEach((f) => {
          const idx = Math.max(0, Math.min(11, Math.round((Number(f.t) || 0) / 5)));
          if (slots[idx].intensity == null) {
            slots[idx] = {
              t: idx * 5,
              intensity: Number(f.intensity) || 1,
              description: f.description || getIntensityDescription(Number(f.intensity) || 1)
            };
          }
        });
        let last = { intensity: 1, description: 'Temps sec' };
        for (let i = 0; i < 12; i++) {
          if (slots[i].intensity == null) {
            slots[i].intensity = last.intensity;
            slots[i].description = last.description;
          } else {
            last = { intensity: slots[i].intensity, description: slots[i].description };
          }
        }
        return slots;
      };

      const normalized = normalizeTo12(forecastData);
      setForecast(normalized);

      if (onRainForecastUpdate) {
        const rainData = {
          willRain: normalized.some(f => f.intensity > 1),
          amount: normalized.length ? normalized.reduce((sum, f) => f.intensity > 1 ? sum + (f.intensity - 1) : sum, 0) / normalized.length : 0,
          amountMax: normalized.length ? Math.max(...normalized.map(f => f.intensity > 1 ? f.intensity - 1 : 0)) : 0,
          probabilityMax: normalized.some(f => f.intensity > 1) ? 80 : 0,
          source: isFrenchPark ? 'meteofrance' : 'openmeteo'
        };
        onRainForecastUpdate(rainData);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données de pluie:', error);
      setError(error.message);
      setForecast([]);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [lat, lon, onRainForecastUpdate, t, isFrenchPark]);

  // Mémoriser la fonction createChart avec useCallback
  const createChart = useCallback(() => {
    const ctx = chartRef.current.getContext('2d');

    // Nettoyer le graphique précédent
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Préparer les données pour le graphique
    const now = new Date();
    const timeLabels = forecast.map(f => {
      const forecastTime = new Date(now.getTime() + f.t * 60000);
      return forecastTime.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    });
    // Labels visibles seulement toutes les 10 minutes
    const labels = forecast.map((f, i) => (f.t % 10 === 0 ? timeLabels[i] : ''));
    const intensities = forecast.map(f => getDisplayIntensity(f));
    const barColors = getBarColors(forecast);

    // Calculer la valeur maximale d'intensité pour l'échelle
    const maxIntensity = Math.max(...intensities, 0);
    let maxYAxis = 5; // Valeur par défaut
    if (maxIntensity > 0) {
      // Arrondir à la valeur supérieure, avec un minimum de 5
      maxYAxis = Math.max(5, Math.ceil(maxIntensity));
    }

    // Créer le graphique
    chartInstance.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Intensité de pluie',
            data: intensities,
            backgroundColor: barColors,
            borderRadius: 6,
            borderSkipped: false,
            barPercentage: 0.8,
            categoryPercentage: 0.8
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const index = context.dataIndex;
                const forecastItem = forecast[index];
                const value = context.parsed.y;
                const description = getIntensityDescription(forecastItem.intensity, forecastItem.description);

                if (forecastItem.intensity === 1 && forecastItem.description === "Temps sec") {
                  return `Temps sec`;
                }

                return `Intensité: ${value} (${description})`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: {
              color: '#fff',
              font: {
                size: 11,
                weight: '500'
              },
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0
            },
            grid: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            max: maxYAxis,
            ticks: {
              color: '#fff',
              font: {
                size: 11,
                weight: '500'
              },
              stepSize: 1
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        }
      }
    });
  }, [forecast]);

  useEffect(() => {
    if (lat && lon) {
      fetchRain();

      // Rafraîchissement automatique toutes les 5 minutes
      const refreshInterval = setInterval(() => {
        fetchRain();
      }, 5 * 60 * 1000); // 5 minutes

      return () => {
        clearInterval(refreshInterval);
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }
      };
    }
  }, [lat, lon, fetchRain]);

  // Effet pour mettre à jour le graphique lorsque les prévisions changent
  useEffect(() => {
    if (forecast && forecast.length > 0 && chartRef.current) {
      createChart();
    }
  }, [forecast, createChart]);

  // Si pas de prévisions, ne pas afficher le widget
  if (!forecast || forecast.length === 0) {
    return null;
  }

  // N'afficher que s'il y a de la pluie détectée (au moins une tranche > 1)
  const hasRain = forecast.some(f => Number(f.intensity) > 1);
  if (!hasRain) {
    return null;
  }

  // Affichage du widget avec les données disponibles
  return (
    <div className="rain-widget">
      <h3>{t('weather.rainForecast', 'Précipitations dans l\'heure')}</h3>
      {error && <p className="rain-widget-error">{t('weather.error', 'Erreur')}: {error}</p>}
      {loading && <p className="rain-widget-loading">{t('weather.loading', 'Chargement...')}</p>}
      <div className="rain-chart-container">
        <canvas ref={chartRef} />
      </div>
      <div className="rain-intensity-legend">
        <div className="intensity-item">
          <div className="intensity-color light"></div>
          <span>{t('weather.lightRain', 'Faibles')}</span>
        </div>
        <div className="intensity-item">
          <div className="intensity-color moderate"></div>
          <span>{t('weather.moderateRain', 'Modérées')}</span>
        </div>
        <div className="intensity-item">
          <div className="intensity-color heavy"></div>
          <span>{t('weather.heavyRain', 'Fortes')}</span>
        </div>
      </div>
    </div>
  );
}